import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DIM_STYLE, LVL_STYLE } from "@/components/Badges";
import { Loader } from "@/components/Loader";
import { track } from "@/lib/track";
import { TopLogoBar } from "@/components/AppShell";

const LEVEL_DESC = {
  ripartire: "Sto ricominciando da qui",
  stabilizzare: "Ho una base, voglio consolidarla",
  potenziare: "Sono solido, voglio migliorare ancora",
};

export default function ManualSelect() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [meta, setMeta] = useState(null);
  const [dimension, setDimension] = useState(null);
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmChange, setConfirmChange] = useState(false);
  const isSub = user?.subscription_active;

  useEffect(() => {
    api.get("/quiz").then(({ data }) => setMeta(data)).catch(() => toast.error("Errore di caricamento"));
  }, []);

  if (!meta) return <Loader />;

  const confirm = async () => {
    // Abbonato: cambia direttamente il percorso (con conferma, azzera i progressi)
    if (isSub) {
      setConfirmChange(true);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/assessment/manual", { dimension, level });
      localStorage.setItem("a2g_result", JSON.stringify(data));
      track("quiz_complete");
      navigate(`/report/${data.profile_id}`, { state: data });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const doChangePath = async () => {
    setLoading(true);
    try {
      await api.post("/path/change", { dimension, level });
      await refreshUser();
      toast.success("Percorso aggiornato! Riparti dal Giorno 1.");
      navigate("/oggi", { replace: true });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setLoading(false);
      setConfirmChange(false);
    }
  };

  return (
    <div className="min-h-screen px-5 pb-28 pt-6" data-testid="manual-select-page">
      <TopLogoBar onBack={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate(user ? "/oggi" : "/"))} testid="manual-back-button" />
      <h1 className="font-serif-el text-2xl font-semibold text-[#FAF9F6]">{isSub ? "Cambia percorso" : "Scegli il tuo focus"}</h1>
      {isSub && <p className="mt-1 text-base text-[#B4B4BC]">Scegli una nuova area: il percorso ripartirà dal Giorno 1.</p>}

      <p className="eyebrow mt-6">Passo 1 · Dimensione</p>
      <h2 className="font-serif-el mt-2 text-2xl font-semibold text-[#FAF9F6]">Cosa vuoi allenare?</h2>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {meta.dimensions.map((d) => {
          const s = DIM_STYLE[d.key];
          const active = dimension === d.key;
          return (
            <button
              key={d.key}
              onClick={() => setDimension(d.key)}
              data-testid={`dimension-choice-${d.key}`}
              className="rounded-2xl border p-4 text-left transition active:scale-[0.98]"
              style={active ? { backgroundColor: `${s.c}18`, borderColor: s.c } : { borderColor: "#27272A", backgroundColor: "#121215" }}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: `${s.c}22`, color: s.c }}>
                <s.icon size={18} />
              </div>
              <p className="mt-2.5 text-lg text-[#F4F4F5] font-semibold">{d.title}</p>
              <p className="mt-0.5 text-sm leading-snug text-[#B4B4BC]">{d.description}</p>
            </button>
          );
        })}
      </div>

      {dimension && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <p className="eyebrow mt-8">Passo 2 · Livello</p>
          <h2 className="font-serif-el mt-2 text-2xl font-semibold text-[#FAF9F6]">A che punto sei?</h2>
          <div className="mt-4 space-y-2.5">
            {meta.levels.map((l) => {
              const s = LVL_STYLE[l.key];
              const active = level === l.key;
              return (
                <button
                  key={l.key}
                  onClick={() => setLevel(l.key)}
                  data-testid={`level-choice-${l.key}`}
                  className="flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition active:scale-[0.98]"
                  style={active ? { backgroundColor: `${s.c}18`, borderColor: s.c } : { borderColor: "#27272A", backgroundColor: "#121215" }}
                >
                  <div>
                    <p className="text-lg text-[#F4F4F5] font-semibold">{l.title}</p>
                    <p className="mt-0.5 text-sm text-[#B4B4BC]">{LEVEL_DESC[l.key]}</p>
                  </div>
                  {active && (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: s.c, color: "#09090B" }}>
                      <Check size={14} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {dimension && level && (
        <div className="fixed inset-x-0 bottom-0 mx-auto max-w-[430px] border-t border-[#27272A] bg-[#09090B]/95 p-4 backdrop-blur-md">
          <button onClick={confirm} disabled={loading} data-testid="manual-confirm-button" className="btn-gold w-full rounded-full px-6 py-4 text-base uppercase tracking-wide">
            {loading ? "Un momento…" : isSub ? "Cambia percorso" : "Vedi il mio profilo"}
          </button>
        </div>
      )}

      {confirmChange && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm" data-testid="change-confirm-overlay" onClick={() => !loading && setConfirmChange(false)}>
          <div className="w-full max-w-[360px] rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-b from-[#141416] to-[#0A0A0C] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif-el text-2xl font-semibold text-[#FAF9F6]">Cambiare percorso?</h3>
            <p className="mt-2 text-base leading-relaxed text-[#B4B4BC]">
              Inizierai un nuovo percorso di 7 giorni dal Giorno 1. Il progresso e la serie di costanza attuali verranno azzerati.
            </p>
            <div className="mt-6 space-y-3">
              <button onClick={doChangePath} disabled={loading} data-testid="change-confirm-button" className="btn-gold w-full rounded-full px-6 py-3.5 text-base uppercase tracking-wide">
                {loading ? "Aggiorno…" : "Sì, cambia percorso"}
              </button>
              <button onClick={() => setConfirmChange(false)} disabled={loading} data-testid="change-cancel-button" className="btn-outline-gold w-full rounded-full px-6 py-3 text-base">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
